import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'

interface FormData {
	nombreApellido: string
	email: string
	telefono: string
	empresa?: string
	descripcion: string
}

interface GeneralFormScreenProps {
	onNext: (data: FormData) => void
	onBack: () => void
	data?: Partial<FormData>
	tipoSeguro?: string
}

export function GeneralFormScreen({
	onNext,
	onBack,
	data,
	tipoSeguro,
}: GeneralFormScreenProps) {
	const [formData, setFormData] = useState<FormData>({
		nombreApellido: data?.nombreApellido || '',
		email: data?.email || '',
		telefono: data?.telefono || '',
		empresa: data?.empresa || '',
		descripcion: data?.descripcion || '',
	})

	const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
		{},
	)

	const validateForm = () => {
		const newErrors: Partial<Record<keyof FormData, string>> = {}

		if (!formData.nombreApellido.trim()) {
			newErrors.nombreApellido = 'El nombre es requerido'
		}
		if (!formData.email.trim()) {
			newErrors.email = 'El email es requerido'
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'Email inválido'
		}
		if (!formData.telefono.trim()) {
			newErrors.telefono = 'El teléfono es requerido'
		}
		if (!formData.descripcion.trim()) {
			newErrors.descripcion = 'Por favor describí qué cobertura necesitás'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-4">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos de contacto
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Completá tus datos para que podamos contactarte
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Nombre y Apellido *
							</label>
							<input
								type="text"
								className={`w-full rounded-xl border px-4 py-3 text-sm ${
									errors.nombreApellido
										? 'border-red-500'
										: 'border-neutral-300'
								}`}
								placeholder="Tu nombre completo"
								value={formData.nombreApellido}
								onChange={(e) =>
									setFormData({ ...formData, nombreApellido: e.target.value })
								}
							/>
							{errors.nombreApellido && (
								<span className="text-xs text-red-500">
									{errors.nombreApellido}
								</span>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Email *
							</label>
							<input
								type="email"
								className={`w-full rounded-xl border px-4 py-3 text-sm ${
									errors.email ? 'border-red-500' : 'border-neutral-300'
								}`}
								placeholder="tu@email.com"
								value={formData.email}
								onChange={(e) =>
									setFormData({ ...formData, email: e.target.value })
								}
							/>
							{errors.email && (
								<span className="text-xs text-red-500">{errors.email}</span>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Teléfono *
							</label>
							<input
								type="tel"
								className={`w-full rounded-xl border px-4 py-3 text-sm ${
									errors.telefono ? 'border-red-500' : 'border-neutral-300'
								}`}
								placeholder="Tu número de teléfono"
								value={formData.telefono}
								onChange={(e) =>
									setFormData({ ...formData, telefono: e.target.value })
								}
							/>
							{errors.telefono && (
								<span className="text-xs text-red-500">{errors.telefono}</span>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Empresa (opcional)
							</label>
							<input
								type="text"
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								placeholder="Nombre de tu empresa"
								value={formData.empresa}
								onChange={(e) =>
									setFormData({ ...formData, empresa: e.target.value })
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								¿Qué cobertura necesitás? *
							</label>
							<textarea
								className={`w-full rounded-xl border px-4 py-3 text-sm ${
									errors.descripcion ? 'border-red-500' : 'border-neutral-300'
								}`}
								rows={4}
								placeholder={
									tipoSeguro
										? `Contanos qué tipo de cobertura de ${tipoSeguro} necesitás, qué querés asegurar, montos aproximados, etc.`
										: 'Contanos qué tipo de seguro necesitás, qué querés asegurar, montos aproximados, etc.'
								}
								value={formData.descripcion}
								onChange={(e) =>
									setFormData({ ...formData, descripcion: e.target.value })
								}
							/>
							{errors.descripcion && (
								<span className="text-xs text-red-500">
									{errors.descripcion}
								</span>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar solicitud
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
